/**
 * Detalle de tarea en Sheet lateral (copiado de timeflow/task-drawer.tsx).
 * La tarea se lee del cache del tablero por id: cualquier mutación optimista
 * o refetch se refleja acá sin estado propio (salvo los textos en edición).
 */
import { useEffect, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useTaskMutations } from '@/hooks/useInternalTasks';
import { TASK_PRIORITY_COLOR, type Task, type TaskProject } from '@/../../shared/internal-tasks';
import { AssigneeSelect, Dot, PrioritySelect, StatusSelect, isOverdue } from './task-fields';
import { fmtDateTime } from '../shared';
import { useTranslation } from 'react-i18next';

export function TaskDrawer({ taskId, tasks, projects, onClose }: {
  taskId: string | null;
  tasks: Task[];
  projects: TaskProject[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const task = taskId ? tasks.find((item) => item.taskId === taskId) ?? null : null;
  const project = task ? projects.find((p) => p.projectId === task.projectId) : undefined;
  const subtasks = task ? tasks.filter((item) => item.parentTaskId === task.taskId) : [];
  const { updateTask, deleteTask } = useTaskMutations();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setTitle(task?.title ?? '');
    setDescription(task?.description ?? '');
  }, [task?.taskId, task?.title, task?.description]);

  // La tarea desapareció del cache (la borró otro admin) → cerrar.
  useEffect(() => { if (taskId && !task) onClose(); }, [taskId, task, onClose]);

  const patch = (fields: Omit<Parameters<typeof updateTask.mutate>[0], 'taskId'>) => {
    if (task) updateTask.mutate({ taskId: task.taskId, ...fields });
  };

  return (
    <Sheet open={!!task} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg">
        {task && (
          <>
            <SheetHeader>
              <SheetTitle className="sr-only">{task.title}</SheetTitle>
              {project && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Dot color={project.color} className="h-2 w-2" />{project.name}
                </div>
              )}
              <Textarea
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => { const t = title.trim(); if (t && t !== task.title) patch({ title: t }); else setTitle(task.title); }}
                rows={2}
                placeholder={t('internal.taskdrawer.titulo_de_la_tarea')}
                className="min-h-0 resize-none border-none bg-transparent p-0 text-lg font-semibold shadow-none focus-visible:ring-0"
              />
            </SheetHeader>

            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-[96px_1fr] items-center gap-x-3 gap-y-2 text-sm">
                <Label className="text-muted-foreground">Estado</Label>
                <StatusSelect value={task.status} onChange={(status) => patch({ status })} />

                <Label className="text-muted-foreground">Prioridad</Label>
                <PrioritySelect value={task.priority} onChange={(priority) => patch({ priority })} />

                <Label className="text-muted-foreground">Responsable</Label>
                <AssigneeSelect value={task.assigneeEmail} onChange={(assigneeEmail) => patch({ assigneeEmail })} />

                <Label className="text-muted-foreground">Vencimiento</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={task.dueDate ?? ''}
                    onChange={(e) => patch({ dueDate: e.target.value || null })}
                    className={cn('h-8 w-fit', isOverdue(task) && 'border-destructive text-destructive')}
                  />
                  {isOverdue(task) && <span className="text-xs text-destructive">Vencida</span>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t('internal.taskdrawer.descripcion')}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => { if (description !== (task.description ?? '')) patch({ description: description.trim() || null }); }}
                  rows={3}
                  placeholder={t('internal.taskdrawer.contexto_links_criterios_de_listo')}
                />
              </div>

              <SubtaskSection task={task} subtasks={subtasks} />
              <ChecklistSection task={task} />

              <div className="border-t pt-4 text-xs text-muted-foreground">
                {t('internal.taskdrawer.creada_el')} {fmtDateTime(task.createdAt)} por {task.createdBy}
              </div>

              <Button
                variant="ghost"
                className="w-full justify-start text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> {t('internal.taskdrawer.eliminar_tarea')}
              </Button>
            </div>
          </>
        )}
      </SheetContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('internal.taskdrawer.eliminar_la_tarea')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('internal.taskdrawer.se_eliminan_tambien_sus')} {subtasks.length} {t('internal.taskdrawer.subtareas_y_el_checklist_esta_accion')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (task) deleteTask.mutate({ taskId: task.taskId }, { onSuccess: onClose }); }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

function SubtaskSection({ task, subtasks }: { task: Task; subtasks: Task[] }) {
  const { t } = useTranslation();
  const { createTask, updateTask, deleteTask } = useTaskMutations();
  const [value, setValue] = useState('');
  const done = subtasks.filter((s) => s.status === 'completed').length;

  const add = () => {
    const title = value.trim();
    if (!title) return;
    setValue('');
    createTask.mutate({ projectId: task.projectId, parentTaskId: task.taskId, title, priority: task.priority });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">Subtareas</Label>
        {subtasks.length > 0 && <span className="text-xs text-muted-foreground">{done}/{subtasks.length}</span>}
      </div>
      <div className="space-y-1">
        {subtasks.map((sub) => (
          <div key={sub.taskId} className="group flex items-center gap-2">
            <Checkbox
              checked={sub.status === 'completed'}
              onCheckedChange={(c) => updateTask.mutate({ taskId: sub.taskId, status: c ? 'completed' : 'pending' })}
            />
            <Dot color={TASK_PRIORITY_COLOR[sub.priority]} className="h-2 w-2" />
            <span className={cn('flex-1 truncate text-sm', sub.status === 'completed' && 'text-muted-foreground line-through')}>
              {sub.title}
            </span>
            <button
              type="button"
              aria-label={t('internal.taskdrawer.eliminar_subtarea')}
              disabled={deleteTask.isPending}
              onClick={() => deleteTask.mutate({ taskId: sub.taskId })}
              className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={t('internal.taskdrawer.agregar_subtarea_enter')}
        className="h-8"
      />
    </div>
  );
}

function ChecklistSection({ task }: { task: Task }) {
  const { t } = useTranslation();
  const { checklistAdd, checklistToggle, checklistRemove } = useTaskMutations();
  const [value, setValue] = useState('');
  const items = task.checklist ?? [];
  const done = items.filter((c) => c.isCompleted).length;
  const progress = items.length ? Math.round((done / items.length) * 100) : 0;

  const add = () => {
    const title = value.trim();
    if (!title) return;
    setValue('');
    checklistAdd.mutate({ taskId: task.taskId, title });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">Checklist</Label>
        {items.length > 0 && <span className="text-xs text-muted-foreground">{done}/{items.length}</span>}
      </div>
      {items.length > 0 && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.id} className="group flex items-center gap-2">
            <Checkbox
              checked={item.isCompleted}
              onCheckedChange={(c) => checklistToggle.mutate({ taskId: task.taskId, itemId: item.id, isCompleted: !!c })}
            />
            <span className={cn('flex-1 truncate text-sm', item.isCompleted && 'text-muted-foreground line-through')}>
              {item.title}
            </span>
            <button
              type="button"
              aria-label={t('internal.taskdrawer.eliminar_item')}
              disabled={checklistRemove.isPending}
              onClick={() => checklistRemove.mutate({ taskId: task.taskId, itemId: item.id })}
              className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={t('internal.taskdrawer.agregar_item_enter')}
        className="h-8"
      />
    </div>
  );
}
